import { type DragEvent, useState } from 'react';
import styled from 'styled-components';
import ExcelUploadDialog from './ExcelUploadDialog';

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  margin-bottom: 10px;

  .govuk-body-s {
    margin: 0;
  }
`;

const Actions = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
`;

export const ExcelActionButton = styled.button<{ $isDragActive?: boolean }>`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 3px;
  border: 0;
  border-radius: 2px;
  color: #00703c;
  background: ${({ $isDragActive }) => ($isDragActive ? '#d2e2f1' : 'transparent')};
  cursor: pointer;

  &:hover {
    color: #004f27;
    background: #f3f2f1;
  }

  &:focus {
    outline: 3px solid transparent;
    color: #0b0c0c;
    background: #ffdd00;
    box-shadow: 0 -2px #ffdd00, 0 4px #0b0c0c;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
  }
`;

export const InlineExcelActionButton = styled(ExcelActionButton)`
  margin: -4px 2px;
  vertical-align: middle;
`;

export const ExcelIcon = () => (
  <svg aria-hidden="true" focusable="false" viewBox="0 0 384 512">
    <path d="M64 0C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H320c35.3 0 64-28.7 64-64V160H256c-17.7 0-32-14.3-32-32V0H64zM256 0V128H384L256 0zM155.7 250.2L192 302.1l36.3-51.9c7.6-10.9 22.6-13.5 33.4-5.9s13.5 22.6 5.9 33.4L221.3 344l46.4 66.2c7.6 10.9 5 25.8-5.9 33.4s-25.8 5-33.4-5.9L192 385.8l-36.3 51.9c-7.6 10.9-22.6 13.5-33.4 5.9s-13.5-25.8-5.9-33.4L162.7 344l-46.4-66.2c-7.6-10.9-5-25.8 5.9-33.4s25.8-5 33.4 5.9z" />
  </svg>
);

export const UploadIcon = () => (
  <svg aria-hidden="true" focusable="false" viewBox="0 0 512 512">
    <path d="M288 109.3V352c0 17.7-14.3 32-32 32s-32-14.3-32-32V109.3l-73.4 73.3c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3l128-128c12.5-12.5 32.8-12.5 45.3 0l128 128c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L288 109.3zM64 352h96v32c0 53 43 96 96 96s96-43 96-96V352h96c35.3 0 64 28.7 64 64v32c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V416c0-35.3 28.7-64 64-64zm368 56a24 24 0 1 0 0 48 24 24 0 1 0 0-48z" />
  </svg>
);

type Props = {
  title: string;
  disabled?: boolean;
  onDownload: () => void | Promise<void>;
  onFiles: (files: File[]) => void | Promise<void>;
};

export default function ExcelToolbar({ title, disabled = false, onDownload, onFiles }: Props) {
  const [isDragActive, setIsDragActive] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>();

  const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsDragActive(false);
    if (!disabled) setUploadFiles(Array.from(event.dataTransfer.files));
  };

  return (
    <Toolbar>
      <span className="govuk-body-s">Bulk import</span>
      <Actions>
        <ExcelActionButton
          type="button"
          aria-label={`Download ${title} as Excel`}
          title={`Download ${title} as Excel`}
          onClick={() => void onDownload()}
        >
          <ExcelIcon />
        </ExcelActionButton>
        <ExcelActionButton
          type="button"
          aria-label={`Upload ${title} from Excel`}
          title={`Upload ${title} from Excel or drop XLSX files here`}
          disabled={disabled}
          $isDragActive={isDragActive}
          onClick={() => setUploadFiles([])}
          onDragEnter={(event) => {
            event.preventDefault();
            setIsDragActive(true);
          }}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={() => setIsDragActive(false)}
          onDrop={handleDrop}
        >
          <UploadIcon />
        </ExcelActionButton>
      </Actions>
      {uploadFiles && (
        <ExcelUploadDialog
          initialFiles={uploadFiles}
          onClose={() => setUploadFiles(undefined)}
          onFiles={onFiles}
        />
      )}
    </Toolbar>
  );
}
